/**
 * Attendance (issue #14) - ONE page: the learner's cross-course history at
 * `/student/attendance`, and a register scoped to one course wherever a course
 * owns it: the registrar's course record (`?view=attendance`) and the course
 * workspace's People view for teaching and learning. It branches on the
 * workspace, then on `useAuth().role` (mirrors CoursesPage.tsx/
 * AnnouncementsPage.tsx's precedent).
 *
 * Role-gating here is UX ONLY: the backend (`verify_instructor` /
 * `verify_instructor_of_session` + the RLS backstop) is the real authority, the
 * same three-layer pattern documented in courses.py:
 *   - admin: the full register matrix for any course, READ-ONLY (no session
 *     generation, no marking; the registrar workflow here is oversight, not
 *     data entry).
 *   - instructor: the course roster with the roll strip (`CourseRoster`): one
 *     register of the enrolled learners, and the roll for one session (the same
 *     `RollRegister` the session portal mounts, sprint decision Q2) for
 *     correcting any session later. Sessions are generated on the Course plan.
 *   - student: their own attendance log, per-course % tiles + a session-by-
 *     session list. `recording_url` is rendered ONLY when the key is physically
 *     present on the object (never a `!== null` check: the backend's gate makes
 *     the key structurally absent for a non-excused mark, see attendance.ts).
 *
 * The session destination is edited on the session portal's time tile only (one
 * fact once); the meeting override that lived here is gone.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { Bento } from "@/components/ui/box";
import type { Box, ListBox } from "@/components/ui/box.schema";
import { registerRead,
  registerText,
  registerPhrases,
} from "@/lib/register-copy";

import { CourseRoster } from "@/components/attendance/roster/CourseRoster";

// The design-system specimen reads the marks group and its key hint from this page's address.
export {
  AttendanceStatusGroup,
  MarkKeysHint,
} from "@/components/attendance/RollRegister";
import { AcademicPageHeader } from "@/components/records/AcademicWorkspace";
import { useWorkspaceEyebrow } from "@/hooks/use-workspace-eyebrow";
import { isHistoricalTerm } from "@/lib/term-state";
import { useAuth } from "@/contexts/AuthContext";
import {
  attendanceApi,
  type CourseAttendance,
  type MyAttendanceSession,
  type MyAttendanceCourse,
} from "@/lib/api/attendance";
import { coursesApi } from "@/lib/api/courses";

export function AttendancePage({
  workspace = "role",
  courseId,
  embedded = false,
  frameBoxes = [],
}: {
  workspace?: "role" | "registrar" | "teaching" | "learning";
  courseId?: string;
  embedded?: boolean;
  frameBoxes?: Box[];
}) {
  const eyebrow = useWorkspaceEyebrow();
  const { role: accountRole } = useAuth();
  const role =
    workspace === "registrar"
      ? "admin"
      : workspace === "teaching"
        ? "instructor"
        : workspace === "learning"
          ? "student"
          : accountRole;

  return (
    <div
      className={
        embedded ? "grid min-w-0 gap-[var(--layout-bento-gap)]" : "app-page"
      }
    >
      {!embedded && (
        <AcademicPageHeader
          eyebrow={eyebrow}
          title={registerText.attendance2}
          description={
            role === "student"
              ? registerText.yourSessionBySessionRecordForEveryCourseWithThe
              : registerText.theRegisterForEachCourseAndTheRollForA
          }
        />
      )}

      {role === "student" ? (
        <StudentAttendance courseId={courseId} frameBoxes={frameBoxes} />
      ) : role === "instructor" ? (
        <CourseRoster courseId={courseId ?? ""} />
      ) : (
        <RegisterAttendance scopedCourseId={courseId} />
      )}
    </div>
  );
}

// ===========================================================================
// Admin: one course's register matrix, read-only
// ===========================================================================

function RegisterAttendance({ scopedCourseId }: { scopedCourseId?: string }) {
  const courses = useQuery({
    queryKey: ["courses"],
    queryFn: () => coursesApi.list(),
  });
  const course = courses.data?.find((entry) => entry.id === scopedCourseId);
  const query = useQuery({
    queryKey: ["attendance", "course", course?.id],
    queryFn: () => attendanceApi.getCourseAttendance(course!.id),
    enabled: Boolean(course),
  });
  if (courses.isError && !courses.data)
    return (
      <p role="alert">
        {registerText.theCourseRegisterIsUnavailable}{" "}
        <button type="button" onClick={() => void courses.refetch()}>
          {registerText.readCoursesAgain}</button>
      </p>
    );
  if (courses.data && !course)
    return (
      <p>
        {registerText.thisCourseRegisterIsUnavailable}{" "}
        <Link to="/admin/courses">{registerText.returnToCourses}</Link>
      </p>
    );
  type Learner = CourseAttendance["roster"][number];
  const held =
    query.data?.sessions.filter((session) => session.status === "held") ?? [];
  const register = registerRead(query, registerText.attendanceRegisters);
  const historical = isHistoricalTerm(course?.semester_status);
  const box: ListBox<CourseAttendance, Learner> = {
    kind: "list",
    id: "attendance-matrix",
    job: registerText.readEachLearnerSMarksAgainstHeldSessions,
    label: registerText.attendanceRegister,
    owner: { system: "imam" },
    fold: "never",
    read: register.status === "ready" && !register.value.roster.length
      ? { status: "empty", sentence: historical
        ? registerPhrases.closedAttendance(course?.semester_name ?? registerText.thisTerm)
        : registerText.noLearnersInThisAttendanceRegister } : register,
    detail: (value) =>
      value.sessions.some((session) => session.status === "held")
        ? registerText.theRecordedMarkForEachLearnerAndHeldSession
        : registerText.noSessionHasBeenHeldYet,
    openDetail: () =>
      isHistoricalTerm(course?.semester_status)
        ? registerText.thisTermIsClosedItsAttendanceRegisterRemainsReadable
        : undefined,
    state: () => ({ class: "quiet", word: registerText.recordedAttendance }),
    figures: (value) => {
      const rates = value.roster.map((row) => row.stats.pct).filter((rate): rate is number => rate !== null);
      return [
      {
        value: value.sessions.filter((session) => session.status === "held")
          .length,
        unit: registerText.heldSessions,
      },
      {
        value: value.sessions.filter(
          (session) => session.status === "scheduled",
        ).length,
        unit: registerText.scheduled,
      },
      { value: value.roster.length, unit: registerText.learners },
      ...(rates.length ? [{ value: Math.round(rates.reduce((sum, rate) => sum + rate, 0) / rates.length * 100), unit: registerText.averageAttendance }] : []),
    ]; },
    rows: (value) => value.roster,
    table: {
      title: registerText.learner,
      columns: [
        ...held.map((session) => ({
          id: session.id,
          label: session.label ?? session.session_date,
        })),
        { id: "rate", label: registerText.attendance2 },
      ],
    },
    row: {
      key: (row) => row.student_id,
      title: (row) => row.preferred_name ?? row.legal_name,
      facts: (row) => (row.participation_kind === "audit" ? registerText.auditor : ""),
      state: () => null,
      cells: (row: Learner) => [
        ...held.map((session) => {
          const mark = row.marks[session.id];
          return {
            id: session.id,
            read: mark?.status
              ? registerPhrases.attendanceMark(registerPhrases.attendance[mark.status], mark.camera_on === false)
              : registerText.notRecorded,
          };
        }),
        {
          id: "rate",
          read:
            row.stats.pct == null
              ? registerText.notYetAvailable
              : `${Math.round(row.stats.pct * 100)}%`,
        },
      ],
    },
  };
  return (
    <Bento
      id="course-attendance"
      journey="course-attendance"
      boxes={[box as Box]}
    />
  );
}

// ===========================================================================
// Student: per-course stat tiles + session log
// ===========================================================================

function StudentAttendance({ courseId, frameBoxes = [] }: { courseId?: string; frameBoxes?: Box[] }) {
  const query = useQuery({
    queryKey: ["attendance", "my"],
    queryFn: attendanceApi.getMyAttendance,
  });
  const all = query.data?.courses ?? [];
  const courses = courseId
    ? all.filter((course) => course.course_id === courseId)
    : all;
  const read = registerRead(query, registerText.attendanceLogs);
  if (courseId && query.data && !courses.length)
    return (
      <p>
        {registerText.theRequestedCourseIsNotInYourAttendanceRecord}{" "}
        <Link data-nav to="/student/attendance">
          {registerText.viewYourAttendance}</Link>
      </p>
    );
  const totals = courses.reduce(
    (sum, course) => ({
      present: sum.present + course.stats.present,
      late: sum.late + course.stats.late,
      absent: sum.absent + course.stats.absent,
      excused: sum.excused + course.stats.excused,
    }),
    { present: 0, late: 0, absent: 0, excused: 0 },
  );
  const marked = totals.present + totals.late + totals.absent + totals.excused;
  const boxes: Box[] = courses.map((course, index) => {
    const courseRead =
      read.status === "ready"
        ? { ...read, value: course }
        : read.status === "stale"
          ? { ...read, last: course }
          : read;
    const box: ListBox<MyAttendanceCourse, MyAttendanceSession> = {
      kind: "list",
      id: `attendance-${course.course_id}`,
      job: registerText.readYourDatedAttendanceMarks,
      label: `${course.code} · ${course.title}`,
      owner: { system: "imam" },
      read: courseRead.status === "ready" && !course.sessions.length
        ? { status: "empty", sentence: registerText.noSessionAttendanceHasBeenRecordedForThisCourse } : courseRead,
      fold: "never",
      detail: () => registerText.yourSessionBySessionAttendanceRecord,
      state: (value) => ({
        class: "quiet",
        word: value.sessions.length
          ? registerText.recordedAttendance
          : registerText.noAttendanceRecordsYet,
      }),
      figures: (value) => [
        ...(value.stats.pct === null
          ? []
          : [
              {
                value: Math.round(value.stats.pct * 100),
                unit: registerText.attendance,
              },
            ]),
        ...(index === 0
          ? [
              { value: courses.length, unit: registerText.courses },
              { value: totals.absent, unit: registerText.absences },
              { value: totals.excused, unit: registerText.excused },
              ...(marked
                ? [
                    {
                      value: Math.round(
                        ((totals.present + totals.late) / marked) * 100,
                      ),
                      unit: registerText.overall,
                    },
                  ]
                : []),
            ]
          : []),
      ],
      rows: (value) => value.sessions,
      row: {
        key: (session) => session.session_id,
        title: (session) => session.label ?? session.session_date,
        state: (session) => ({
          class: session.status === null ? "quiet" : session.status === "absent" ? "waiting" : "done",
          word: session.status === null ? registerText.notRecorded : registerPhrases.attendance[session.status],
        }),
        facts: (session) =>
          `${session.label ? session.session_date : ""}${session.camera_on === false ? registerText.cameraOff2 : ""}`,
        act: (session) =>
          session.status === "excused"
            ? { does: "open", verb: registerText.recording }
            : undefined,
        detail: (session) => <RecordingCell session={session} />,
      },
    };
    return box as Box;
  });
  if (!boxes.length) {
    const empty: ListBox<unknown, never> = {
      kind: "list",
      id: "attendance-log",
      job: registerText.readYourAttendanceRecord,
      label: registerText.attendanceLog,
      owner: { system: "imam" },
      read: read.status === "ready" ? { status: "empty", sentence: registerText.noAttendanceRecordsYetAttendanceAppearsAfterAnInstructorRecords } : read,
      fold: "never",
      detail: () => registerText.attendanceAppearsAfterAnInstructorRecordsASession,
      state: () => ({ class: "quiet", word: registerText.noAttendanceRecordsYet }),
      rows: () => [],
      row: {
        key: () => "",
        title: () => "",
        state: () => ({ class: "quiet", word: "" }),
      },
    };
    boxes.push(empty as Box);
  }
  return (
    <Bento
      id="student-attendance"
      journey={`student-attendance-${courseId ?? "all"}`}
      boxes={[...frameBoxes, ...boxes]}
    />
  );
}

function RecordingCell({ session }: { session: MyAttendanceSession }) {
  if (session.status !== "excused")
    return (
      <span className="text-meta text-muted-foreground">{registerText.notAvailable}</span>
    );
  if ("recording_url" in session && session.recording_url) {
    return (
      <a
        data-act="leave"
        href={session.recording_url}
        target="_blank"
        rel="noreferrer"
        className="text-ui font-medium underline"
        style={{ color: "var(--primary)" }}
      >
        {registerText.watchRecordingOpensInANewTab}</a>
    );
  }
  return (
    <span className="text-meta text-muted-foreground">{registerText.recordingWithheld}</span>
  );
}
